/**
 * A REGRA de férias e 13º salário. Módulo PURO.
 *
 * Importa `folhaDp` porque INSS e IRRF são os mesmos — reescrevê-los aqui
 * criaria a segunda definição do mesmo imposto. Nenhum valor de negócio mora
 * neste arquivo; tudo entra por `ParametrosFolha`.
 *
 * ---------------------------------------------------------------------------
 * A DIVERGÊNCIA que ficou nomeada: a redução do IR não vale aqui
 * ---------------------------------------------------------------------------
 * A origem aplica a redução mensal do IRPF de 2026 **no holerite e em mais nada**
 * — férias e 13º saem com o imposto puro de tabela. O `CLAUDE.md` de lá chama
 * isso de "decisão de produto".
 *
 * Eu acho que está errado para as FÉRIAS: elas são rendimento tributável do mês
 * como qualquer outro, e a redução incide sobre os rendimentos tributáveis. Para
 * o 13º a discussão é outra, porque ele tem tributação exclusiva na fonte, com
 * regra própria.
 *
 * **Preservei o comportamento da origem de propósito.** Mudar isto altera o
 * imposto retido de gente real, nas duas direções, e não é decisão que se toma
 * dentro de um porte — está no runbook como pendência para Victor, com o número:
 * nas férias de quem ganha até R$ 5.000, a diferença é de até R$ 312,89 por
 * evento. O caminho está pronto: é passar `aplicarReducao = true` nas três
 * chamadas de `compararModelosIrrf` deste arquivo.
 */

import {
  ALIQUOTA_FGTS,
  DIAS_MES,
  centavos,
  calcularInss,
  compararModelosIrrf,
  type ModeloIrrf,
  type ParametrosFolha,
  type ResultadoInss,
  type ResultadoIrrf,
} from "./folhaDp";

/** Um terço constitucional (CF art. 7º, XVII). Estrutural, não expira. */
export const TERCO_CONSTITUCIONAL = 1 / 3;

/** Dias de férias do período aquisitivo completo (CLT art. 130). */
export const DIAS_FERIAS = 30;

/** Máximo de dias vendidos: 1/3 dos 30 (CLT art. 143). */
export const MAX_DIAS_ABONO = 10;

/** Meses do ano, para os avos do 13º. */
export const AVOS_ANO = 12;

/**
 * A redução do IR não é aplicada a férias nem ao 13º — ver o cabeçalho.
 * Constante nomeada, e não `false` solto nas chamadas, para a decisão ter um
 * lugar só e um nome quando alguém for revê-la.
 */
const APLICAR_REDUCAO_IR = false;

// ---------------------------------------------------------------------------
// Férias
// ---------------------------------------------------------------------------

export interface EntradaFerias {
  salarioBase: number;
  /** Médias de horas extras, comissões e adicionais do período aquisitivo. */
  mediaVariaveis: number;
  /** Dias de gozo, 1 a 30. */
  diasGozo: number;
  /** Vender dias (abono pecuniário, CLT art. 143). */
  venderDias: boolean;
  diasVendidos: number;
  dependentes: number;
  modeloIrrf: ModeloIrrf;
  /** Adiantar a 1ª parcela do 13º junto das férias (Lei 4.749/65 art. 2º, §2º). */
  adiantarDecimo: boolean;
}

export interface ResultadoFerias {
  remuneracao: number;
  ferias: number;
  terco: number;
  abono: number;
  tercoAbono: number;
  adiantamentoDecimo: number;
  totalBruto: number;
  /** Base de INSS e IRRF: férias + terço, SEM o abono. */
  baseTributavel: number;
  inss: ResultadoInss;
  irrf: { completo: ResultadoIrrf; simplificado: ResultadoIrrf; vantajoso: ModeloIrrf; aplicado: ResultadoIrrf };
  liquido: number;
  /** True quando o aluno pediu mais dias vendidos do que a lei permite. */
  abonoLimitado: boolean;
}

/**
 * Férias com o terço constitucional e, se houver, o abono pecuniário.
 *
 * **O abono e o terço dele NÃO sofrem INSS nem IRRF.** É a peça que mais dói se
 * estiver errada, e é o motivo de a base tributável ser calculada em separado do
 * total bruto: o abono é verba INDENIZATÓRIA (não é contraprestação por trabalho),
 * então não integra salário de contribuição — art. 28, §9º, "e", 6, da Lei
 * 8.212/91, e a Súmula 125 do STJ para o IR. Tributar o abono é o erro clássico
 * de folha, e o módulo o evita por construção, não por lembrança.
 *
 * O adiantamento do 13º é **metade da remuneração**, como a Lei 4.749/65 diz —
 * "metade do salário do mês anterior" —, e não metade do 13º proporcional. Quem
 * tem menos de 12 avos recebe a mesma metade e acerta na 2ª parcela; é assim que
 * a lei desenhou, e é por isso que este formulário não pergunta avos. Ele
 * também não sofre desconto agora: os descontos do 13º inteiro saem na 2ª
 * parcela (ver `calcularDecimo`).
 */
export function calcularFerias(e: EntradaFerias, p: ParametrosFolha): ResultadoFerias {
  const remuneracao = Math.max(0, e.salarioBase) + Math.max(0, e.mediaVariaveis);
  const diaria = remuneracao / DIAS_MES;

  const diasGozo = Math.min(DIAS_FERIAS, Math.max(0, e.diasGozo));
  const ferias = centavos(diaria * diasGozo);
  const terco = centavos(ferias * TERCO_CONSTITUCIONAL);

  const pedidos = e.venderDias ? Math.max(0, e.diasVendidos) : 0;
  const diasVendidos = Math.min(MAX_DIAS_ABONO, pedidos);
  const abono = centavos(diaria * diasVendidos);
  const tercoAbono = centavos(abono * TERCO_CONSTITUCIONAL);

  const adiantamentoDecimo = e.adiantarDecimo ? centavos(remuneracao / 2) : 0;

  // A base tributável exclui abono e terço do abono — ver o comentário acima.
  const baseTributavel = centavos(ferias + terco);
  const inss = calcularInss(baseTributavel, p);
  const comparacao = compararModelosIrrf(
    baseTributavel,
    inss.valor,
    e.dependentes,
    p,
    APLICAR_REDUCAO_IR,
  );
  const aplicado = e.modeloIrrf === "completo" ? comparacao.completo : comparacao.simplificado;

  const totalBruto = centavos(ferias + terco + abono + tercoAbono + adiantamentoDecimo);

  return {
    remuneracao: centavos(remuneracao),
    ferias,
    terco,
    abono,
    tercoAbono,
    adiantamentoDecimo,
    totalBruto,
    baseTributavel,
    inss,
    irrf: { ...comparacao, aplicado },
    liquido: centavos(totalBruto - inss.valor - aplicado.valor),
    abonoLimitado: pedidos > MAX_DIAS_ABONO,
  };
}

// ---------------------------------------------------------------------------
// 13º salário
// ---------------------------------------------------------------------------

export interface EntradaDecimo {
  salarioBase: number;
  mediaVariaveis: number;
  /** Avos: mês com 15 dias ou mais de trabalho conta 1 (Lei 4.090/62 art. 1º, §2º). */
  avos: number;
  dependentes: number;
  modeloIrrf: ModeloIrrf;
  /**
   * O que já foi pago na 1ª parcela.
   *
   * `null` é "não informado — assuma a metade", que é o caso comum. `0` é "não
   * houve adiantamento", que é diferente e existe de verdade: quem foi admitido
   * em dezembro não recebeu 1ª parcela nenhuma.
   *
   * **Esta é uma correção sobre a origem**, e é a única mudança de resultado
   * deste módulo. Lá o campo era `number` e o código fazia
   * `adiantamentoRecebido > 0 ? adiantamentoRecebido : primeira` — ou seja, quem
   * digitasse 0 querendo dizer "não recebi" tinha a 2ª parcela abatida de um
   * adiantamento que nunca existiu, e o líquido saía menor sem nenhum aviso. Não
   * havia como expressar "zero" na tela.
   */
  adiantamentoRecebido: number | null;
}

export interface ResultadoDecimo {
  remuneracao: number;
  avos: number;
  bruto: number;
  /** Metade do bruto, sem descontos, até 30/11 (ou nas férias, se pedido). */
  primeiraParcela: number;
  /** O que foi considerado como já pago — informado, ou a metade. */
  adiantamentoConsiderado: number;
  segundaParcela: {
    /** O que resta a pagar antes dos descontos. */
    bruto: number;
    inss: ResultadoInss;
    irrf: { completo: ResultadoIrrf; simplificado: ResultadoIrrf; vantajoso: ModeloIrrf; aplicado: ResultadoIrrf };
    liquido: number;
  };
  /** 8% sobre o 13º bruto — depósito, não desconto. */
  fgts: number;
}

/**
 * O 13º em avos, com a 2ª parcela já líquida.
 *
 * **Os descontos incidem sobre o 13º INTEGRAL, e saem todos na 2ª parcela.** A 1ª
 * é adiantamento puro (Lei 4.749/65 art. 2º), e o INSS/IRRF do 13º é calculado
 * uma vez, sobre o valor cheio, na 2ª — não metade em cada. Dividir os descontos
 * pelas duas parcelas é erro comum e dá imposto MENOR que o devido, porque a
 * progressividade seria aplicada duas vezes sobre metades.
 *
 * E o IRRF do 13º é **exclusivo na fonte**: ele não se soma ao salário do mês
 * para efeito de tabela, e não entra no ajuste anual. Por isso a base aqui é o
 * 13º isolado, e não 13º + salário de dezembro.
 */
export function calcularDecimo(e: EntradaDecimo, p: ParametrosFolha): ResultadoDecimo {
  const remuneracao = Math.max(0, e.salarioBase) + Math.max(0, e.mediaVariaveis);
  const avos = Math.min(AVOS_ANO, Math.max(0, Math.round(e.avos)));
  const bruto = centavos((remuneracao / AVOS_ANO) * avos);
  const primeiraParcela = centavos(bruto / 2);

  // `null` = não informado, assume a metade. `0` = não houve adiantamento, e é
  // respeitado — ver o comentário em `adiantamentoRecebido`.
  const adiantamentoConsiderado =
    e.adiantamentoRecebido === null ? primeiraParcela : Math.max(0, e.adiantamentoRecebido);

  const inss = calcularInss(bruto, p);
  const comparacao = compararModelosIrrf(bruto, inss.valor, e.dependentes, p, APLICAR_REDUCAO_IR);
  const aplicado = e.modeloIrrf === "completo" ? comparacao.completo : comparacao.simplificado;

  const restante = centavos(bruto - adiantamentoConsiderado);

  return {
    remuneracao: centavos(remuneracao),
    avos,
    bruto,
    primeiraParcela,
    adiantamentoConsiderado,
    segundaParcela: {
      bruto: restante,
      inss,
      irrf: { ...comparacao, aplicado },
      liquido: centavos(restante - inss.valor - aplicado.valor),
    },
    fgts: centavos(bruto * ALIQUOTA_FGTS),
  };
}

/**
 * Avos a partir das datas de admissão e do fim do período — sem chamar o
 * relógio.
 *
 * `agora` entra por PARÂMETRO em vez de `new Date()` dentro, para a função poder
 * ser provada com qualquer data. É a disciplina de `anosDaDasn` no simulador do
 * MEI, e pela mesma razão: função que lê o relógio não tem prova estável.
 *
 * A regra é a da Lei 4.090/62: mês com **15 dias ou mais** de trabalho conta um
 * avo inteiro. Para quem trabalhou o ano todo dá 12.
 */
export function avosNoAno(admissao: Date, agora: Date): number {
  const ano = agora.getFullYear();
  if (admissao.getFullYear() > ano) return 0;

  // Admitido em ano anterior: todos os meses até o mês corrente contam.
  if (admissao.getFullYear() < ano) return Math.min(AVOS_ANO, agora.getMonth() + 1);

  const mesAdmissao = admissao.getMonth();
  // O mês da admissão só conta se sobraram 15 dias ou mais de trabalho nele.
  const diasNoMes = new Date(ano, mesAdmissao + 1, 0).getDate();
  const diasTrabalhados = diasNoMes - admissao.getDate() + 1;
  const contaOMesDeAdmissao = diasTrabalhados >= 15;

  const meses = agora.getMonth() - mesAdmissao + (contaOMesDeAdmissao ? 1 : 0);
  return Math.min(AVOS_ANO, Math.max(0, meses));
}
